'use client';

import type { DeviceType } from '@weberus/shared-types';
import { useCallback, useMemo, type FC } from 'react';
import {
  cn,
  Divider,
  IconButton,
  MessageBox,
  ProductBadge,
  RectangleButton,
  Stepper,
  Tab,
  TextButton,
} from '@yanolja-rnd/nol-design-system';
import { IcAdd, IcMyFill, IcTrashcan } from '@yanolja-rnd/nol-icons';
import { produce } from 'immer';
import { throttle } from 'lodash-es';
import { AccordionSelectBox } from './accordion-select-box';

export interface RoomCapacity {
  capacityAdult: number;
  capacityChild: number;
  capacityChildAges: number[];
}

export interface CapacitySelectProps {
  isOpen: boolean;
  rooms: RoomCapacity[];
  adultCount?: {
    max?: number;
    min?: number;
  };
  childrenCount?: {
    max?: number;
    min: number;
  };
  /** 해외숙소인 경우 객실 단위 인원선택 노출 */
  isGlobal?: boolean;
  /** 인원 open 시 */
  onOpenChange: (isOpen: boolean) => void;
  /** 인원 변경 시 */
  onCapacityChange: (rooms: RoomCapacity[]) => void;
  /** 아동 나이 스크롤 중 인지 체크, 바텀시트와 스크롤 충돌하지 않기 위함 */
  onScrollingChange?: (isScrolling: boolean) => void;
  deviceType?: DeviceType;
  className?: string;
}

const childrenAgesTab = ['만 1세 미만', ...Array.from({ length: 17 }).map((_, index) => `만 ${index + 1}세`)];
const DEFAULT_MAX_ADULT = 30;
const DEFAULT_MIN_ADULT = 1;
const DEFAULT_MAX_CHILDREN = 10;
const DEFAULT_MIN_CHILDREN = 0;

const DEFAULT_CAPACITY_ADULT = 2;
const DEFAULT_CAPACITY_CHILDREN = 0;
export const DEFAULT_CAPACITY_ROOMS = [
  {
    capacityAdult: DEFAULT_CAPACITY_ADULT,
    capacityChild: DEFAULT_CAPACITY_CHILDREN,
    capacityChildAges: [],
  },
];

export const CapacitySelect: FC<CapacitySelectProps> = (props) => {
  const {
    isOpen,
    rooms,
    deviceType,
    className,
    isGlobal = false,
    adultCount,
    childrenCount,
    onOpenChange,
    onCapacityChange,
    onScrollingChange,
  } = props;
  const isPc = deviceType === 'etc';

  const capacityCountText = useMemo(() => {
    const totalCount = rooms.reduce(
      (acc, { capacityAdult, capacityChild }) => ({
        adults: acc.adults + capacityAdult,
        childs: acc.childs + capacityChild,
      }),
      { adults: 0, childs: 0 },
    );

    const roomsCountText = isGlobal ? `객실 ${rooms.length}, ` : '';
    const adultCountText = `성인 ${totalCount.adults}`;
    const childCountText = totalCount.childs > 0 ? `, 아동 ${totalCount.childs}` : '';

    return `${roomsCountText}${adultCountText}${childCountText}`;
  }, [isGlobal, rooms]);

  const addRoom = useCallback(() => {
    const updatedRooms = produce(rooms, (draft) => {
      draft.push(DEFAULT_CAPACITY_ROOMS[0]);
    });
    onCapacityChange(updatedRooms);
  }, [rooms, onCapacityChange]);

  const removeRoom = useCallback(
    (roomIdx: number) => {
      if (rooms.length <= 1) return; // 최소 한 개의 객실은 유지

      const updatedRooms = produce(rooms, (draft) => {
        draft.splice(roomIdx, 1);
      });
      onCapacityChange(updatedRooms);
    },
    [rooms, onCapacityChange],
  );

  const handleScroll = useMemo(
    () =>
      throttle((isScrolling: boolean) => {
        onScrollingChange?.(isScrolling);
      }, 50),
    [onScrollingChange],
  );

  return (
    <AccordionSelectBox
      deviceType={deviceType}
      isOpen={isOpen}
      className={className}
      onHeaderClick={() => onOpenChange(!isOpen)}>
      {{
        header: (
          <div className={cn('flex size-full items-center gap-6')}>
            <IcMyFill className={cn('shrink-0', isPc ? 'size-24' : 'size-20')} />
            <span
              className={cn('w-full truncate', isPc ? 'typography-subtitle-18-bold' : 'typography-subtitle-16-bold')}>
              {capacityCountText}
            </span>
            {!isOpen && (
              <TextButton theme="underline" className="shrink-0">
                변경하기
              </TextButton>
            )}
          </div>
        ),
        body: (
          <div className={cn('h-full overflow-y-auto bg-static-white pb-8')}>
            {rooms.map((room, roomIdx) => (
              <div key={roomIdx}>
                {!!isGlobal && (
                  <div className="flex min-h-36 items-center justify-between px-16 py-2">
                    <ProductBadge className="bg-opacity-black-60">객실 {roomIdx + 1}</ProductBadge>
                    {rooms.length > 1 && (
                      <IconButton theme="ghost" size={isPc ? 'medium' : 'small'} onClick={() => removeRoom(roomIdx)}>
                        <IcTrashcan className={cn('size-20 text-text-neutral-medium', isPc ? 'size-24' : 'size-20')} />
                      </IconButton>
                    )}
                  </div>
                )}
                <div className={cn('flex items-center justify-between py-4', isPc ? 'h-68 px-20' : 'h-58 px-16')}>
                  <div className="flex flex-col gap-4">
                    <span className={isPc ? 'typography-subtitle-18-bold' : 'typography-subtitle-16-bold'}>성인</span>
                    {!!isGlobal && (
                      <span
                        className={cn(
                          'text-text-neutral-sub',
                          isPc ? 'typography-body-14-regular' : 'typography-body-12-regular',
                        )}>
                        만 18세 이상
                      </span>
                    )}
                  </div>
                  <Stepper
                    value={room.capacityAdult}
                    max={adultCount?.max ?? DEFAULT_MAX_ADULT}
                    min={adultCount?.min ?? DEFAULT_MIN_ADULT}
                    onChange={(value) => {
                      const newValue = produce(rooms, (draft) => {
                        draft[roomIdx].capacityAdult = value;
                      });
                      onCapacityChange?.(newValue);
                    }}
                  />
                </div>

                <div className={cn('flex items-center justify-between py-4', isPc ? 'h-68 px-20' : 'h-58 px-16')}>
                  <div className="flex flex-col gap-4">
                    <span className={isPc ? 'typography-subtitle-18-bold' : 'typography-subtitle-16-bold'}>아동</span>
                    {!!isGlobal && (
                      <span
                        className={cn(
                          'text-text-neutral-sub',
                          isPc ? 'typography-body-14-regular' : 'typography-body-12-regular',
                        )}>
                        만 17세 이하
                      </span>
                    )}
                  </div>
                  <Stepper
                    value={room.capacityChild}
                    max={childrenCount?.max ?? DEFAULT_MAX_CHILDREN}
                    min={childrenCount?.min ?? DEFAULT_MIN_CHILDREN}
                    onChange={(value) => {
                      const newValue = produce(rooms, (draft) => {
                        draft[roomIdx].capacityChild = value;
                        draft[roomIdx].capacityChildAges =
                          value > room.capacityChild
                            ? [...room.capacityChildAges, 0]
                            : room.capacityChildAges.slice(0, value);
                      });
                      onCapacityChange?.(newValue);
                    }}
                  />
                </div>

                {room.capacityChild > 0 && (
                  <div className={cn('flex flex-col py-12', isPc ? 'gap-12' : 'gap-8')}>
                    {!!isGlobal && (
                      <MessageBox theme="info" className="mx-16">
                        <span
                          className={
                            isPc ? 'typography-subtitle-16-regular-multiline' : 'typography-body-13-regular-multiline'
                          }>
                          체크아웃 시점의 아동 나이로 선택해주세요.
                        </span>
                      </MessageBox>
                    )}

                    {Array.from({ length: room.capacityChild }).map((_, index) => (
                      <div key={index} className={cn('flex flex-col py-8', isPc ? 'gap-10' : 'gap-8')}>
                        <span className="px-16">
                          <ProductBadge type="filled_basic" size={{ pc: 'large', mobile: isPc ? 'large' : 'medium' }}>
                            아동 {index + 1}
                          </ProductBadge>
                        </span>
                        <Tab.Chip
                          shapeType="outlined"
                          deviceType={deviceType}
                          selectedIndex={room.capacityChildAges[index]}
                          items={childrenAgesTab}
                          swiperOptions={{
                            spaceBetween: isPc ? 8 : 6,
                            slidesOffsetBefore: isPc ? 20 : 16,
                            slidesOffsetAfter: isPc ? 20 : 16,
                            slideStepCount: isPc ? 5 : 1,
                          }}
                          setSelectedIndex={(selectedIndex) => {
                            const newValue = produce(rooms, (draft) => {
                              draft[roomIdx].capacityChildAges[index] = selectedIndex;
                            });
                            onCapacityChange?.(newValue);
                          }}
                          swiperProps={{
                            onTouchStart: () => {
                              handleScroll(true);
                            },
                            onTouchEnd: () => {
                              handleScroll(false);
                            },
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {roomIdx < rooms.length - 1 && <Divider size="small" className="my-10" />}
              </div>
            ))}

            {!!isGlobal && (
              <div className="px-16 py-8 pc:px-20">
                <RectangleButton
                  disabled={rooms.length >= 8}
                  theme="outlined_basic"
                  size={{
                    mobile: isPc ? 'medium' : 'small',
                    pc: isPc ? 'medium' : 'small',
                  }}
                  fullWidth
                  onClick={addRoom}>
                  <div className="flex items-center gap-2">
                    <span>객실 추가</span>
                    <IcAdd className="size-14" />
                  </div>
                </RectangleButton>
              </div>
            )}
          </div>
        ),
      }}
    </AccordionSelectBox>
  );
};
